import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { serviceKeys, type ServiceKey } from "../api/staff";

const PERMISSION_OPTIONS = [
  "payments:create",
  "payments:read",
  "invoices:read",
  "boosts:manage",
  "subscriptions:manage",
];

export function ServiceKeyCreate() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [role, setRole] = useState<ServiceKey["role"]>("dev");
  const [permissions, setPermissions] = useState<string[]>(["payments:create"]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    plaintextKey: string;
    record: ServiceKey;
    validationRunId: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  function togglePermission(p: string) {
    setPermissions((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const out = await serviceKeys.create({ name, role, permissions });
      setResult(out);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  }

  function goToValidation() {
    if (!result?.validationRunId) return;
    navigate(`/service-keys/${result.record.id}/runs/${result.validationRunId}`);
  }

  if (result) {
    return (
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Key created</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Copy the plaintext now — it will never be shown again. The
            key is currently <code>pending</code> and unusable. Validate
            it on the next step to activate.
          </p>
        </div>
        <div className="card border-amber-500/30">
          <div className="mb-2 text-xs uppercase text-amber-300">
            Plaintext key (shown once)
          </div>
          <div className="break-all rounded-md bg-bg p-3 font-mono text-sm">
            {result.plaintextKey}
          </div>
          <button
            className="btn-ghost mt-3 text-xs"
            onClick={() => navigator.clipboard.writeText(result.plaintextKey)}
          >
            Copy to clipboard
          </button>
        </div>
        <button
          className="btn-primary"
          disabled={!result.validationRunId}
          onClick={goToValidation}
        >
          Open terminal &rarr;
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-xl">
      <h1 className="mb-1 text-2xl font-semibold">New service key</h1>
      <p className="mb-6 text-sm text-text-secondary">
        The key will be created in <code>pending</code> and a validation
        run will start automatically.
      </p>
      <form onSubmit={submit} className="card space-y-5">
        <div>
          <label className="label">Name</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="payment-worker (eu-west-1)"
            required
            maxLength={80}
          />
        </div>
        <div>
          <label className="label">Role</label>
          <select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as ServiceKey["role"])}
          >
            <option value="admin">admin</option>
            <option value="dev">dev</option>
            <option value="service">service</option>
          </select>
        </div>
        <div>
          <label className="label">Permissions</label>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {PERMISSION_OPTIONS.map((p) => (
              <label
                key={p}
                className="flex items-center gap-2 rounded-md border border-white/5 bg-bg-elevated px-3 py-2"
              >
                <input
                  type="checkbox"
                  checked={permissions.includes(p)}
                  onChange={() => togglePermission(p)}
                />
                <code className="text-sm">{p}</code>
              </label>
            ))}
          </div>
        </div>
        {error ? (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        ) : null}
        <button
          type="submit"
          disabled={busy || name.trim().length === 0 || permissions.length === 0}
          className="btn-primary"
        >
          {busy ? "Creating…" : "Create key"}
        </button>
      </form>
    </div>
  );
}
